import { createReducer } from '@reduxjs/toolkit'
import {
  CodeValidationAgent,
  CodeValidationStatus,
  CodeValidationDiscipline,
  Task,
  CodeValidationRule,
  CodeValidationRuleStatus,
} from '../models/Validation'
import {
  deselectCodeValidationRule,
  deselectCodeValidationStatus,
  deselectCodeValidationDiscipline,
  deselectFilterByCodeValidationStatusesForDiscipline,
  selectCodeValidationRule,
  selectCodeValidationStatus,
  selectCodeValidationDiscipline,
  selectFilterByCodeValidationStatusesForDiscipline,
  setCodeValidationAgent,
  setIsCodeValidationProcessingStarted,
  setShowCodeValidationOptionsModal,
  setCurrentValidationTask,
  addCodeValidationRulesForDiscipline,
  resetCodeValidationState,
  setIsCodeValidationStatusChecking,
  updateCodeValidationRulesWithStatus,
  setCurrentValildationMessages,
  setIsAllCodeValidationRulesFetched,
  selectCodeValidationRuleElement,
  selectCodeValidationItem,
} from '../actions/validation'
import { getEnums } from '../utils/Common'

interface ValidationState {
  codeValidationRules: Record<CodeValidationDiscipline, CodeValidationRule[]>
  selectedCodeValidationRule: CodeValidationRule | null
  selectedCodeValidationItem: string | null
  selectedCodeValidationDisciplines: CodeValidationDiscipline[]
  selectedCodeValidationStatuses: CodeValidationStatus[]
  selectedCodeValidationAgent: CodeValidationAgent
  filteredCodeValidationStatusesGroupedByDiscipline: Record<
    CodeValidationDiscipline,
    CodeValidationStatus[]
  >
  isShowOptionsModal: boolean
  isCodeValidationStatusChecking: boolean
  isCodeValidationProcessingStarted: boolean
  currentValidationTask: Task | null
  currentValidationMessages: string[]
  isAllCodeValidationRulesFetched: boolean
  selectedCodeValidationRuleElement: CodeValidationRuleStatus | null
}

const initialState: ValidationState = {
  codeValidationRules: {
    [CodeValidationDiscipline.Architectural]: [],
    [CodeValidationDiscipline.Structural]: [],
    [CodeValidationDiscipline.City]: [],
    [CodeValidationDiscipline.MEP]: [],
    [CodeValidationDiscipline.Fire]: [],
    [CodeValidationDiscipline.Other]: [],
  },
  selectedCodeValidationRule: null,
  selectedCodeValidationItem: null,
  selectedCodeValidationDisciplines: [],
  selectedCodeValidationStatuses: [],
  selectedCodeValidationAgent: CodeValidationAgent.AutoAgent,
  filteredCodeValidationStatusesGroupedByDiscipline: {
    [CodeValidationDiscipline.Architectural]: [],
    [CodeValidationDiscipline.Structural]: [],
    [CodeValidationDiscipline.City]: [],
    [CodeValidationDiscipline.MEP]: [],
    [CodeValidationDiscipline.Fire]: [],
    [CodeValidationDiscipline.Other]: [],
  },
  isShowOptionsModal: false,
  isCodeValidationStatusChecking: false,
  isCodeValidationProcessingStarted: false,
  currentValidationTask: null,
  currentValidationMessages: [],
  isAllCodeValidationRulesFetched: false,
  selectedCodeValidationRuleElement: null,
}

const validationReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(addCodeValidationRulesForDiscipline, (state, action) => {
      const rawRules = [
        ...state.codeValidationRules[action.payload.discipline],
        ...action.payload.rules,
      ]

      const ruleIds = new Set<string>()
      const rules = rawRules.reduce<CodeValidationRule[]>((acc, current) => {
        if (!ruleIds.has(current.id)) {
          acc.push(current)
          ruleIds.add(current.id)
        }

        return acc
      }, [])

      state.codeValidationRules = {
        ...state.codeValidationRules,
        [action.payload.discipline]: rules,
      }
    })
    .addCase(updateCodeValidationRulesWithStatus, (state, action) => {
      const data = action.payload.data
      const disciplines = getEnums(CodeValidationDiscipline).map(
        ([_, discipline]) => discipline,
      )
      console.log(
        'codeValidationRules',
        state.codeValidationRules.architectural,
      )
      console.log('data', data)

      for (const discipline of disciplines) {
        const rulesByDiscipline = state.codeValidationRules[discipline]
        const items = data.filter((item) => item.discipline === discipline)
        if (!items.length || !rulesByDiscipline.length) {
          continue
        }

        const updatedRules = rulesByDiscipline.map((rule) => {
          const itemsWithSameRuleId = items.filter(
            (item) => item.ruleId === rule.id,
          )

          for (const item of itemsWithSameRuleId) {
            if (rule.status !== CodeValidationStatus.Failed) {
              rule.status = item.status
            }

            if (!rule.elementsIds) {
              rule.elementsIds = []
            }
            if (!rule.elements) {
              rule.elements = []
            }

            rule.elementsIds.push(item.elementId)
            rule.elements.push(item)
          }

          return rule
        })

        state.codeValidationRules = {
          ...state.codeValidationRules,
          [discipline]: updatedRules,
        }
        console.log('Update rules')
      }
    })
    .addCase(selectCodeValidationRule, (state, action) => {
      state.selectedCodeValidationRule = action.payload.item
      state.isShowOptionsModal = false
    })
    .addCase(selectCodeValidationItem, (state, action) => {
      state.selectedCodeValidationItem = action.payload.item
    })
    .addCase(selectCodeValidationRuleElement, (state, action) => {
      state.selectedCodeValidationRuleElement = action.payload.item
    })
    .addCase(deselectCodeValidationRule, (state) => {
      state.selectedCodeValidationRule = null
      state.isShowOptionsModal = false
    })
    .addCase(selectCodeValidationDiscipline, (state, action) => {
      const set = new Set(state.selectedCodeValidationDisciplines)
      set.add(action.payload.value)
      state.selectedCodeValidationDisciplines = Array.from(set)
    })
    .addCase(deselectCodeValidationDiscipline, (state, action) => {
      const set = new Set(state.selectedCodeValidationDisciplines)
      set.delete(action.payload.value)
      state.selectedCodeValidationDisciplines = Array.from(set)
    })
    .addCase(selectCodeValidationStatus, (state, action) => {
      const set = new Set(state.selectedCodeValidationStatuses)
      set.add(action.payload.value)
      state.selectedCodeValidationStatuses = Array.from(set)
    })
    .addCase(deselectCodeValidationStatus, (state, action) => {
      const set = new Set(state.selectedCodeValidationStatuses)
      set.delete(action.payload.value)
      state.selectedCodeValidationStatuses = Array.from(set)
    })
    .addCase(
      selectFilterByCodeValidationStatusesForDiscipline,
      (state, action) => {
        const { discipline, status } = action.payload
        const set = new Set(
          state.filteredCodeValidationStatusesGroupedByDiscipline[discipline],
        )
        set.add(status)
        state.filteredCodeValidationStatusesGroupedByDiscipline = {
          ...state.filteredCodeValidationStatusesGroupedByDiscipline,
          [discipline]: Array.from(set),
        }
      },
    )
    .addCase(
      deselectFilterByCodeValidationStatusesForDiscipline,
      (state, action) => {
        const { discipline, status } = action.payload
        const set = new Set(
          state.filteredCodeValidationStatusesGroupedByDiscipline[discipline],
        )
        set.delete(status)
        state.filteredCodeValidationStatusesGroupedByDiscipline = {
          ...state.filteredCodeValidationStatusesGroupedByDiscipline,
          [discipline]: Array.from(set),
        }
      },
    )
    .addCase(setCodeValidationAgent, (state, action) => {
      state.selectedCodeValidationAgent = action.payload.agent
    })
    .addCase(setShowCodeValidationOptionsModal, (state, action) => {
      state.isShowOptionsModal = action.payload
    })
    .addCase(setIsCodeValidationStatusChecking, (state, action) => {
      state.isCodeValidationStatusChecking = action.payload
    })
    .addCase(setIsCodeValidationProcessingStarted, (state, action) => {
      state.isCodeValidationProcessingStarted = action.payload
    })
    .addCase(setCurrentValidationTask, (state, action) => {
      state.currentValidationTask = action.payload
    })
    .addCase(setCurrentValildationMessages, (state, action) => {
      state.currentValidationMessages = action.payload
    })
    .addCase(resetCodeValidationState, (state) => {
      return { ...initialState }
    })
    .addCase(setIsAllCodeValidationRulesFetched, (state, action) => {
      state.isAllCodeValidationRulesFetched = action.payload
    })
})

export default validationReducer
